import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  resolvePilotRoleFeatureFlags,
  type PilotOnboardingProgramRoleConfiguration,
} from '@shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Calendar,
  Card,
  CardContent,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@design-system';
import {
  ArrowLeft,
  CalendarIcon,
  ChevronRight,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import { preRegisteredFeatureFlagDefinitions } from '@/components/FeatureFlagsEditor';
import { useDebounce } from '@/hooks/useDebounce';
import { fromDateOnlyString, toDateOnlyString } from '@/shared/lib/utils';
import { getErrorMessage } from '@/features/wizard-advisor-admin/utils/get-error-message';
import {
  pilotOnboardingApi,
  pilotOnboardingKeys,
  type PilotAdvisorSearchResult,
  type PilotProgram,
} from './pilot-onboarding-api';
import {
  dateInputValue,
  dateTimeValue,
  formatPilotDate,
  normalizeEmail,
  pilotAdvisorName,
} from './use-pilot-onboarding-admin';

type EditableRole = PilotOnboardingProgramRoleConfiguration & { featureFlags: FeatureFlags };

type RoleDraft = {
  originalKey: string | null;
  key: string;
  label: string;
  shortLabel: string;
  defaultSubscriptionOwnerEmail: string;
  featureFlags: FeatureFlags;
};

function selectedFlagDefinitions(flags: FeatureFlags) {
  return preRegisteredFeatureFlagDefinitions.filter(definition => flags[definition.key]);
}

function rolesForComparison(roles: EditableRole[]) {
  return roles.map(role => ({
    ...role,
    featureFlags: Object.fromEntries(
      Object.entries(role.featureFlags).sort(([left], [right]) => left.localeCompare(right)),
    ),
  }));
}

function roleDraftFromRole(role: EditableRole): RoleDraft {
  return {
    originalKey: role.key,
    key: role.key,
    label: role.label,
    shortLabel: role.shortLabel,
    defaultSubscriptionOwnerEmail: role.defaultSubscriptionOwnerEmail,
    featureFlags: { ...role.featureFlags },
  };
}

function createRoleKey(label: string, roles: EditableRole[]): string {
  const base =
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'role';
  let key = base;
  let suffix = 2;
  while (roles.some(role => role.key === key)) key = `${base}-${suffix++}`;
  return key;
}

export function PilotPartnerSettings({
  program,
  onBack,
  onSaved,
}: {
  program: PilotProgram;
  onBack: () => void;
  onSaved: (program: PilotProgram) => void;
}) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(program.displayName);
  const [shortName, setShortName] = useState(program.shortName);
  const [expiresDate, setExpiresDate] = useState(
    dateInputValue(program.configuration.defaultSeatExpiresDateTime),
  );
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [roles, setRoles] = useState<EditableRole[]>(() =>
    program.configuration.roles.map(role => ({
      ...role,
      featureFlags: resolvePilotRoleFeatureFlags(
        program.configuration.featureFlags,
        role.featureFlags,
      ),
    })),
  );
  const [supervisorEmails, setSupervisorEmails] = useState(() =>
    program.configuration.defaultSupervisors
      .filter(supervisor => supervisor.isActive)
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map(supervisor => supervisor.email),
  );
  const [knownAdvisors, setKnownAdvisors] = useState<PilotAdvisorSearchResult[]>(
    program.referencedAdvisors,
  );
  const [supervisorQuery, setSupervisorQuery] = useState('');
  const [roleDraft, setRoleDraft] = useState<RoleDraft | null>(null);
  const [ownerQuery, setOwnerQuery] = useState('');
  const [flagQuery, setFlagQuery] = useState('');
  const [pendingRoleDelete, setPendingRoleDelete] = useState<EditableRole | null>(null);
  const [pendingRoleRename, setPendingRoleRename] = useState<RoleDraft | null>(null);
  const [pendingSupervisorRemoval, setPendingSupervisorRemoval] = useState<string | null>(null);
  const [showDeletedRoles, setShowDeletedRoles] = useState(false);
  const [showUnsavedChanges, setShowUnsavedChanges] = useState(false);

  const debouncedSupervisorQuery = useDebounce(supervisorQuery.trim(), 300);
  const supervisorSearchQuery = useQuery({
    queryKey: pilotOnboardingKeys.advisorSearch('relationship', debouncedSupervisorQuery),
    queryFn: () => pilotOnboardingApi.searchAdvisors(debouncedSupervisorQuery, 'relationship'),
    enabled: debouncedSupervisorQuery.length >= 2,
    retry: false,
  });
  const debouncedOwnerQuery = useDebounce(ownerQuery.trim(), 300);
  const ownerSearchQuery = useQuery({
    queryKey: pilotOnboardingKeys.advisorSearch('subscription_owner', debouncedOwnerQuery),
    queryFn: () => pilotOnboardingApi.searchAdvisors(debouncedOwnerQuery, 'subscription_owner'),
    enabled: !!roleDraft && debouncedOwnerQuery.length >= 2,
    retry: false,
  });

  const referencedAdvisorByEmail = useMemo(
    () => new Map(knownAdvisors.map(advisor => [normalizeEmail(advisor.advisorEmail), advisor])),
    [knownAdvisors],
  );
  const activeRoles = roles.filter(role => role.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  const deletedRoles = roles
    .filter(role => !role.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const supervisorResults = (supervisorSearchQuery.data ?? []).filter(
    advisor => !supervisorEmails.includes(normalizeEmail(advisor.advisorEmail)),
  );
  const ownerResults = ownerSearchQuery.data ?? [];
  const currentOwner = roleDraft
    ? (ownerResults.find(
        advisor =>
          normalizeEmail(advisor.advisorEmail) ===
          normalizeEmail(roleDraft.defaultSubscriptionOwnerEmail),
      ) ??
      referencedAdvisorByEmail.get(normalizeEmail(roleDraft.defaultSubscriptionOwnerEmail)) ??
      null)
    : null;
  const ownerOptions = roleDraft
    ? [
        ...(roleDraft.defaultSubscriptionOwnerEmail
          ? [
              {
                email: normalizeEmail(roleDraft.defaultSubscriptionOwnerEmail),
                advisor: currentOwner,
              },
            ]
          : []),
        ...ownerResults
          .filter(
            advisor =>
              normalizeEmail(advisor.advisorEmail) !==
              normalizeEmail(roleDraft.defaultSubscriptionOwnerEmail),
          )
          .map(advisor => ({
            email: normalizeEmail(advisor.advisorEmail),
            advisor,
          })),
      ]
    : [];
  const visibleFlags = [...preRegisteredFeatureFlagDefinitions]
    .filter(definition => {
      const search = flagQuery.trim().toLowerCase();
      return (
        !search ||
        definition.key.toLowerCase().includes(search) ||
        definition.label.toLowerCase().includes(search)
      );
    })
    .sort(
      (left, right) =>
        Number(!!roleDraft?.featureFlags[right.key]) - Number(!!roleDraft?.featureFlags[left.key]),
    );

  const currentSignature = JSON.stringify({
    displayName,
    shortName,
    expiresDate,
    roles: rolesForComparison(roles),
    supervisorEmails,
  });
  const initialSignature = useMemo(
    () =>
      JSON.stringify({
        displayName: program.displayName,
        shortName: program.shortName,
        expiresDate: dateInputValue(program.configuration.defaultSeatExpiresDateTime),
        roles: rolesForComparison(
          program.configuration.roles.map(role => ({
            ...role,
            featureFlags: resolvePilotRoleFeatureFlags(
              program.configuration.featureFlags,
              role.featureFlags,
            ),
          })),
        ),
        supervisorEmails: program.configuration.defaultSupervisors
          .filter(supervisor => supervisor.isActive)
          .sort((left, right) => left.sortOrder - right.sortOrder)
          .map(supervisor => supervisor.email),
      }),
    [program],
  );
  const isDirty = currentSignature !== initialSignature;
  const canSave =
    !!displayName.trim() &&
    !!shortName.trim() &&
    !!expiresDate &&
    activeRoles.length > 0 &&
    activeRoles.every(
      role => !!role.defaultSubscriptionOwnerEmail && Object.keys(role.featureFlags).length > 0,
    );

  const saveMutation = useMutation({
    mutationFn: () =>
      pilotOnboardingApi.updateProgram(program.id, {
        displayName: displayName.trim(),
        shortName: shortName.trim(),
        expectedConfigVersion: program.configVersion,
        configuration: {
          ...program.configuration,
          defaultSeatExpiresDateTime: dateTimeValue(expiresDate),
          roles: roles.map((role, index) => ({
            ...role,
            label: role.label.trim(),
            shortLabel: role.shortLabel.trim(),
            defaultSubscriptionOwnerEmail: normalizeEmail(role.defaultSubscriptionOwnerEmail),
            featureFlags: resolvePilotRoleFeatureFlags(
              program.configuration.featureFlags,
              role.featureFlags,
            ),
            sortOrder: index,
          })),
          defaultSupervisors: supervisorEmails.map((email, index) => ({
            email: normalizeEmail(email),
            accessType: 'no_client_access' as const,
            isActive: true,
            sortOrder: index,
          })),
        },
      }),
    onSuccess: updated => {
      queryClient.setQueryData<PilotProgram[]>(pilotOnboardingKeys.programs(), current =>
        current?.map(candidate => (candidate.id === updated.id ? updated : candidate)),
      );
      toast.success('Pilot partner settings saved');
      onSaved(updated);
    },
  });

  function requestBack() {
    if (isDirty) setShowUnsavedChanges(true);
    else onBack();
  }

  function openRole(role?: EditableRole) {
    setOwnerQuery('');
    setFlagQuery('');
    setRoleDraft(
      role
        ? roleDraftFromRole(role)
        : {
            originalKey: null,
            key: '',
            label: '',
            shortLabel: '',
            defaultSubscriptionOwnerEmail: '',
            featureFlags: { ...program.configuration.featureFlags },
          },
    );
  }

  function applyRole(draft: RoleDraft) {
    if (draft.originalKey) {
      setRoles(current =>
        current.map(role =>
          role.key === draft.originalKey
            ? {
                ...role,
                label: draft.label.trim(),
                shortLabel: draft.shortLabel.trim(),
                defaultSubscriptionOwnerEmail: normalizeEmail(draft.defaultSubscriptionOwnerEmail),
                featureFlags: { ...draft.featureFlags },
              }
            : role,
        ),
      );
    } else {
      const key = createRoleKey(draft.label || draft.shortLabel, roles);
      setRoles(current => [
        ...current,
        {
          key,
          label: draft.label.trim(),
          shortLabel: draft.shortLabel.trim(),
          defaultSubscriptionOwnerEmail: normalizeEmail(draft.defaultSubscriptionOwnerEmail),
          featureFlags: { ...draft.featureFlags },
          isActive: true,
          sortOrder: current.length,
        },
      ]);
    }
    setPendingRoleRename(null);
    setRoleDraft(null);
  }

  function requestRoleSave() {
    if (!roleDraft) return;
    const existing = roleDraft.originalKey
      ? roles.find(role => role.key === roleDraft.originalKey)
      : null;
    if (
      existing &&
      (existing.label !== roleDraft.label.trim() ||
        existing.shortLabel !== roleDraft.shortLabel.trim())
    ) {
      setPendingRoleRename(roleDraft);
      return;
    }
    applyRole(roleDraft);
  }

  function supervisorDisplay(email: string): string {
    const advisor =
      referencedAdvisorByEmail.get(normalizeEmail(email)) ??
      supervisorSearchQuery.data?.find(
        candidate => normalizeEmail(candidate.advisorEmail) === normalizeEmail(email),
      );
    return advisor ? pilotAdvisorName(advisor) : email;
  }

  const pendingSupervisorName = pendingSupervisorRemoval
    ? supervisorDisplay(pendingSupervisorRemoval)
    : '';
  const pendingSupervisorFirstName = pendingSupervisorName.split(' ')[0];

  return (
    <>
      <div className="space-y-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <button
                  type="button"
                  onClick={requestBack}
                  className="text-primary inline-flex items-center gap-1"
                >
                  <ArrowLeft className="size-4" />
                  Onboard pilot user
                </button>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{program.displayName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-start justify-between gap-4">
          <h2 className="text-foreground text-xl font-semibold">{program.displayName}</h2>
          <div className="flex flex-col items-end gap-2">
            <Button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={!isDirty || !canSave || saveMutation.isPending}
            >
              {saveMutation.isPending && <Spinner />}
              Save changes
            </Button>
            {saveMutation.isError && (
              <p className="text-right text-sm text-red-600">
                {getErrorMessage(
                  saveMutation.error as Error,
                  'Pilot partner settings could not be saved.',
                )}
              </p>
            )}
          </div>
        </div>

        <Card className="rounded-[14px] border shadow-sm">
          <CardContent className="grid gap-5 p-6">
            <div>
              <h3 className="text-foreground font-semibold">Details</h3>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="pilot-partner-name">Partner name</Label>
                <Input
                  id="pilot-partner-name"
                  value={displayName}
                  onChange={event => setDisplayName(event.target.value)}
                  placeholder="e.g. Edward Jones"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="pilot-partner-short-name">Short name</Label>
                <Input
                  id="pilot-partner-short-name"
                  value={shortName}
                  onChange={event => setShortName(event.target.value.toUpperCase().slice(0, 10))}
                  placeholder="EDJ"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="pilot-partner-expiry">Pilot end date</Label>
                <Popover
                  open={calendarOpen}
                  onOpenChange={setCalendarOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      id="pilot-partner-expiry"
                      type="button"
                      variant="outline"
                      className="justify-start gap-2 font-normal"
                    >
                      <CalendarIcon className="size-4" />
                      {expiresDate ? formatPilotDate(dateTimeValue(expiresDate)) : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={fromDateOnlyString(expiresDate)}
                      defaultMonth={fromDateOnlyString(expiresDate)}
                      onSelect={date => {
                        if (!date) return;
                        setExpiresDate(toDateOnlyString(date));
                        setCalendarOpen(false);
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-[14px] border shadow-sm">
          <CardContent className="p-0">
            <div className="flex items-start justify-between gap-4 p-6">
              <div>
                <h3 className="text-foreground font-semibold">Roles</h3>
                <p className="text-muted-foreground mt-1 text-sm">
                  Each role sets the subscription owner and pre-registered feature flags for new
                  invitations.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => openRole()}
              >
                <Plus className="size-4" /> Add role
              </Button>
            </div>
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="px-6 font-semibold">Role</TableHead>
                  <TableHead className="font-semibold">Subscription owner</TableHead>
                  <TableHead className="font-semibold">Pre-registered feature flags</TableHead>
                  <TableHead className="w-14" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeRoles.map(role => {
                  const owner = referencedAdvisorByEmail.get(
                    normalizeEmail(role.defaultSubscriptionOwnerEmail),
                  );
                  return (
                    <TableRow key={role.key}>
                      <TableCell className="px-6 py-4">
                        <p className="font-medium">{role.label}</p>
                        <p className="text-muted-foreground text-xs">{role.shortLabel}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">
                          {owner ? pilotAdvisorName(owner) : role.defaultSubscriptionOwnerEmail}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {role.defaultSubscriptionOwnerEmail}
                        </p>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {selectedFlagDefinitions(role.featureFlags)
                          .map(definition => definition.label)
                          .join(' · ')}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={`Edit ${role.label}`}
                          onClick={() => openRole(role)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {deletedRoles.length > 0 && (
              <Collapsible
                open={showDeletedRoles}
                onOpenChange={setShowDeletedRoles}
                className="border-t"
              >
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-auto w-full justify-start rounded-none px-6 py-4"
                  >
                    <ChevronRight
                      className={`size-4 transition-transform ${showDeletedRoles ? 'rotate-90' : ''}`}
                    />
                    {deletedRoles.length} deleted {deletedRoles.length === 1 ? 'role' : 'roles'}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <ItemGroup>
                    {deletedRoles.map((role, index) => (
                      <div key={role.key}>
                        {index > 0 && <ItemSeparator />}
                        <Item className="px-6">
                          <ItemContent>
                            <ItemTitle>{role.label}</ItemTitle>
                            <ItemDescription>{role.shortLabel}</ItemDescription>
                          </ItemContent>
                          <ItemActions>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setRoles(current =>
                                  current.map(candidate =>
                                    candidate.key === role.key
                                      ? { ...candidate, isActive: true }
                                      : candidate,
                                  ),
                                )
                              }
                            >
                              <RotateCcw className="size-4" /> Restore
                            </Button>
                          </ItemActions>
                        </Item>
                      </div>
                    ))}
                  </ItemGroup>
                </CollapsibleContent>
              </Collapsible>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-[14px] border shadow-sm">
          <CardContent className="grid gap-4 p-6">
            <div>
              <h3 className="text-foreground font-semibold">Supervisors</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                Added to every new {displayName || program.displayName} invitation with no client
                access. Changes apply to future invitations only.
              </p>
            </div>
            <InputGroup>
              <InputGroupAddon>
                <Search />
              </InputGroupAddon>
              <InputGroupInput
                aria-label="Search supervisors"
                value={supervisorQuery}
                onChange={event => setSupervisorQuery(event.target.value)}
                placeholder="Add a supervisor by name or email"
              />
            </InputGroup>
            {supervisorSearchQuery.isFetching && (
              <p className="text-muted-foreground flex items-center gap-2 text-xs">
                <Spinner className="size-3.5" /> Searching…
              </p>
            )}
            {supervisorResults.length > 0 && (
              <ItemGroup className="max-h-52 overflow-y-auto rounded-lg border">
                {supervisorResults.map(advisor => (
                  <Item
                    asChild
                    key={advisor.advisorId}
                    size="sm"
                    className="w-full flex-nowrap text-left"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setKnownAdvisors(current =>
                          current.some(candidate => candidate.advisorId === advisor.advisorId)
                            ? current
                            : [...current, advisor],
                        );
                        setSupervisorEmails(current => [
                          ...current,
                          normalizeEmail(advisor.advisorEmail),
                        ]);
                        setSupervisorQuery('');
                      }}
                    >
                      <ItemContent className="min-w-0 items-start text-left">
                        <ItemTitle>{pilotAdvisorName(advisor)}</ItemTitle>
                        <ItemDescription className="w-full text-left">
                          {advisor.advisorEmail}
                        </ItemDescription>
                      </ItemContent>
                      <Plus className="size-4" />
                    </button>
                  </Item>
                ))}
              </ItemGroup>
            )}
            {supervisorEmails.length > 0 && (
              <ItemGroup className="overflow-hidden rounded-lg border">
                {supervisorEmails.map((supervisorEmail, index) => (
                  <div key={supervisorEmail}>
                    {index > 0 && <ItemSeparator />}
                    <Item size="sm">
                      <ItemContent>
                        <ItemTitle>{supervisorDisplay(supervisorEmail)}</ItemTitle>
                        <ItemDescription>{supervisorEmail}</ItemDescription>
                      </ItemContent>
                      <ItemActions>
                        <span className="text-muted-foreground text-sm">No client access</span>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          aria-label={`Remove ${supervisorEmail}`}
                          onClick={() => setPendingSupervisorRemoval(supervisorEmail)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </ItemActions>
                    </Item>
                  </div>
                ))}
              </ItemGroup>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={!!roleDraft}
        onOpenChange={open => !open && setRoleDraft(null)}
      >
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
          <DialogHeader className="px-6 pb-4 pt-6">
            <DialogTitle>
              {roleDraft?.originalKey
                ? `Edit role · ${roleDraft.label}`
                : `Add role · ${displayName || program.displayName}`}
            </DialogTitle>
          </DialogHeader>
          {roleDraft && (
            <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto px-6 pb-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="pilot-role-name">Role name</Label>
                  <Input
                    id="pilot-role-name"
                    value={roleDraft.label}
                    placeholder="e.g. Branch Office Administrator"
                    onChange={event =>
                      setRoleDraft(current =>
                        current ? { ...current, label: event.target.value } : current,
                      )
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="pilot-role-short-name">Short name</Label>
                  <Input
                    id="pilot-role-short-name"
                    value={roleDraft.shortLabel}
                    placeholder="BOA"
                    onChange={event =>
                      setRoleDraft(current =>
                        current
                          ? {
                              ...current,
                              shortLabel: event.target.value.toUpperCase().slice(0, 6),
                            }
                          : current,
                      )
                    }
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <div className="flex items-baseline justify-between gap-3">
                  <Label>Pre-registered feature flags</Label>
                  <span className="text-muted-foreground text-xs">
                    {selectedFlagDefinitions(roleDraft.featureFlags).length} selected
                  </span>
                </div>
                <InputGroup>
                  <InputGroupAddon>
                    <Search />
                  </InputGroupAddon>
                  <InputGroupInput
                    aria-label="Search feature flags"
                    value={flagQuery}
                    onChange={event => setFlagQuery(event.target.value)}
                    placeholder="Search pre-registered feature flags"
                  />
                </InputGroup>
                <ItemGroup className="max-h-56 overflow-y-auto rounded-lg border">
                  {visibleFlags.map((definition, index) => {
                    const checked = !!roleDraft.featureFlags[definition.key];
                    return (
                      <div key={definition.key}>
                        {index > 0 && <ItemSeparator />}
                        <Item
                          asChild
                          size="sm"
                        >
                          <label className="cursor-pointer">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={nextChecked =>
                                setRoleDraft(current => {
                                  if (!current) return current;
                                  const featureFlags = { ...current.featureFlags };
                                  if (nextChecked) featureFlags[definition.key] = true;
                                  else delete featureFlags[definition.key];
                                  return { ...current, featureFlags };
                                })
                              }
                            />
                            <ItemContent>
                              <ItemTitle>{definition.label}</ItemTitle>
                              <ItemDescription>{definition.description}</ItemDescription>
                            </ItemContent>
                          </label>
                        </Item>
                      </div>
                    );
                  })}
                  {visibleFlags.length === 0 && (
                    <p className="text-muted-foreground p-4 text-center text-[13px]">
                      No flags match “{flagQuery}”.
                    </p>
                  )}
                </ItemGroup>
              </div>

              <div className="grid gap-2">
                <Label>Subscription owner</Label>
                <p className="text-muted-foreground text-[13px]">
                  Search advisors who own an active subscription.
                </p>
                <InputGroup>
                  <InputGroupAddon>
                    <Search />
                  </InputGroupAddon>
                  <InputGroupInput
                    aria-label="Search subscription owners"
                    value={ownerQuery}
                    onChange={event => setOwnerQuery(event.target.value)}
                    placeholder="Search by name or email"
                  />
                </InputGroup>
                {ownerSearchQuery.isFetching && (
                  <p className="text-muted-foreground flex items-center gap-2 text-xs">
                    <Spinner className="size-3.5" /> Searching…
                  </p>
                )}
                {ownerOptions.length > 0 && (
                  <RadioGroup
                    value={normalizeEmail(roleDraft.defaultSubscriptionOwnerEmail)}
                    onValueChange={nextEmail => {
                      const advisor = ownerResults.find(
                        candidate => normalizeEmail(candidate.advisorEmail) === nextEmail,
                      );
                      if (advisor) {
                        setKnownAdvisors(current =>
                          current.some(candidate => candidate.advisorId === advisor.advisorId)
                            ? current
                            : [...current, advisor],
                        );
                      }
                      setRoleDraft(current =>
                        current
                          ? {
                              ...current,
                              defaultSubscriptionOwnerEmail: nextEmail,
                            }
                          : current,
                      );
                    }}
                    className="max-h-44 gap-0 overflow-y-auto rounded-lg border"
                  >
                    {ownerOptions.map((option, index) => {
                      const optionName = option.advisor
                        ? pilotAdvisorName(option.advisor)
                        : option.email;
                      const optionId = `pilot-role-owner-${encodeURIComponent(option.email)}`;
                      return (
                        <div key={option.email}>
                          {index > 0 && <ItemSeparator />}
                          <Item
                            asChild
                            size="sm"
                          >
                            <Label
                              htmlFor={optionId}
                              className="cursor-pointer font-normal"
                            >
                              <RadioGroupItem
                                id={optionId}
                                value={option.email}
                                aria-label={optionName}
                              />
                              <ItemContent>
                                <ItemTitle>{optionName}</ItemTitle>
                                {option.advisor && (
                                  <ItemDescription>{option.advisor.advisorEmail}</ItemDescription>
                                )}
                              </ItemContent>
                            </Label>
                          </Item>
                        </div>
                      );
                    })}
                  </RadioGroup>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="border-t px-6 py-4 sm:justify-between">
            <div>
              {roleDraft?.originalKey && activeRoles.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    const role = roles.find(candidate => candidate.key === roleDraft.originalKey);
                    if (role) setPendingRoleDelete(role);
                  }}
                >
                  <Trash2 className="size-4" /> Delete role
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRoleDraft(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={requestRoleSave}
                disabled={
                  !roleDraft?.label.trim() ||
                  !roleDraft.shortLabel.trim() ||
                  !roleDraft.defaultSubscriptionOwnerEmail ||
                  Object.keys(roleDraft.featureFlags).length === 0
                }
              >
                {roleDraft?.originalKey ? 'Save' : 'Add role'}
              </Button>
            </div>
          </DialogFooter>
          {roleDraft?.originalKey && (
            <p className="text-muted-foreground border-t px-6 py-3 text-xs">
              Changes apply to future invitations only. Existing seats stay where they are.
            </p>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingRoleDelete}
        onOpenChange={open => !open && setPendingRoleDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pendingRoleDelete?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              It will be hidden from new invitations. People already invited as{' '}
              {pendingRoleDelete?.shortLabel} keep their role, seats and relationships. Restore it
              anytime from Deleted roles under the role list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                if (!pendingRoleDelete) return;
                setRoles(current =>
                  current.map(role =>
                    role.key === pendingRoleDelete.key ? { ...role, isActive: false } : role,
                  ),
                );
                setPendingRoleDelete(null);
                setRoleDraft(null);
              }}
            >
              Delete role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingRoleRename}
        onOpenChange={open => !open && setPendingRoleRename(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rename role?</AlertDialogTitle>
            <AlertDialogDescription>
              “{roles.find(role => role.key === pendingRoleRename?.originalKey)?.label} (
              {roles.find(role => role.key === pendingRoleRename?.originalKey)?.shortLabel})” will
              become “{pendingRoleRename?.label} ({pendingRoleRename?.shortLabel})” everywhere,
              including existing invitations and the list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => pendingRoleRename && applyRole(pendingRoleRename)}>
              Rename role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingSupervisorRemoval}
        onOpenChange={open => !open && setPendingSupervisorRemoval(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {pendingSupervisorName}?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingSupervisorName} won’t be added as a supervisor on new{' '}
              {displayName || program.displayName} invitations. People already invited keep their
              existing relationship with {pendingSupervisorFirstName}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                if (!pendingSupervisorRemoval) return;
                setSupervisorEmails(current =>
                  current.filter(email => email !== pendingSupervisorRemoval),
                );
                setPendingSupervisorRemoval(null);
              }}
            >
              Remove supervisor
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <UnsavedChangesDialog
        showWarning={showUnsavedChanges}
        handleCancelClose={() => setShowUnsavedChanges(false)}
        handleConfirmClose={onBack}
        confirmButtonTestId="pilot-partner-settings-unsaved-confirm"
      />
    </>
  );
}
